import React, { useState, useEffect } from "react";
import dynamic from "next/dynamic";
const Paystack = dynamic(() => import("../common/Paystack"), { ssr: false });

import CartItem from "./CartItem";

import CartSummary from "./CartSummary";
import {
  getLoggedinUser,
  useCheckUserMembership,
  useFreeTicketRegister,
  usePaidTicketRegister,
} from "../../lib/util";
import { useFormatCurrency } from "../common/format-currency";
import AuthForm from "../auth/AuthForm";
import RegisterForm from "./RegisterForm";
import ImageComponent from "../common/ImageComponent";
import UserLoginSwitch from "../auth/UserLoginSwitch";
import ThankYou from "../common/ThankYou";
import { useAppSelector } from "../slice/hooks";
import { getActiveUser } from "../slice/auth-slice";

const Cart = ({ event }) => {
  const { formatCurrency } = useFormatCurrency();
  const [bundles, setBundles] = useState(
    event.bundles.map((bundle) => ({
      ...bundle,
      cartQuantity: bundle.cartQuantity || 0,
    })),
  );
  const [error, setError] = useState("");

  const { loggedInUser } = useAppSelector((state) =>
    getActiveUser(state, getLoggedinUser()),
  );

  const [showLogin, setShowLogin] = useState(false);
  const [noSignUp, setNoSignUp] = useState(false);
  const [checkoutSuccess, setCheckoutSuccess] = useState(false);
  const [isMember, setIsMember] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [paidTickets, setPaidTickets] = useState(false);
  const [data, setData] = useState({});

  const [paymentInfo, setPaymentInfo] = useState({
    amount: 0,
    email: "",
    reference: "",
    publicKey: "",
    transaction_charge: 0,
  });

  const currency = event.currency || "USD";

  const { isLoading: isLoadingFree, mutate: mutateFree } =
    useFreeTicketRegister({
      onError: (response) => {
        setError(response.message);
      },
      onSuccess: async (response) => {
        setCheckoutSuccess(true);
      },
    });

  const { isLoading: isLoadingPaid, mutate: mutateInitializePayment } =
    usePaidTicketRegister({
      onError: (response) => {
        setError(response.message);
      },
      onSuccess: async (response) => {
        const {
          access,
          paymentNo,
          amountToCharge,
          transactionEmail,
          transactionCharge,
        } = response.data;
        setPaymentInfo({
          amount: amountToCharge,
          email: transactionEmail,
          reference: paymentNo,
          publicKey: access.key,
          transaction_charge: transactionCharge,
          channels: ["card"],
        });
      },
    });

  const {
    isLoading: isLoadingCheckUserMembership,
    mutate: mutateCheckUserMembership,
  } = useCheckUserMembership({
    onError: (response) => {
      // setError(response.message);
    },
    onSuccess: async (response) => {
      setIsMember(response.data.member);
    },
  });

  useEffect(() => {
    if (loggedInUser && loggedInUser.user) {
      mutateCheckUserMembership({
        userId: loggedInUser.user.userId,
        orgId: event.host.profileId,
      });
    }
  }, [event.host.profileId, loggedInUser]);

  const handleIncrease = (bundleId) => {
    setError("");
    setBundles((prevBundles) =>
      prevBundles.map((bundle) =>
        bundle._id === bundleId &&
        bundle.cartQuantity < event.maxTicketPerUser &&
        bundle.cartQuantity < bundle.quantity - bundle.ticketSold
          ? { ...bundle, cartQuantity: bundle.cartQuantity + 1 }
          : bundle,
      ),
    );
  };

  const handleDecrease = (bundleId) => {
    setError("");
    setBundles((prevBundles) =>
      prevBundles.map((bundle) =>
        bundle._id === bundleId && bundle.cartQuantity > 0
          ? { ...bundle, cartQuantity: bundle.cartQuantity - 1 }
          : bundle,
      ),
    );
  };

  const calculateTotal = () => {
    return bundles.reduce(
      (acc, bundle) => acc + bundle.amount * bundle.cartQuantity,
      0,
    );
  };

  const handleSubmitOrder = () => {
    const selectedTickets = bundles
      .filter((bundle) => bundle.cartQuantity > 0)
      .map((bundle) => ({
        bundleId: bundle._id,
        name: bundle.name,
        quantity: bundle.cartQuantity,
        amount: bundle.amount,
      }));

    if (selectedTickets.length === 0) {
      setError("Please select at least one ticket.");
      return;
    }

    const hasPaidTickets = selectedTickets.some((ticket) => ticket.amount > 0);

    setTickets(selectedTickets);
    setPaidTickets(hasPaidTickets);

    if (!loggedInUser || !loggedInUser.user) {
      setShowLogin(true);
      return;
    }

    if (!hasPaidTickets) {
      mutateFree({
        eventId: event.eventId,
        userId: loggedInUser.user.userId,
        ticketItems: selectedTickets,
        channel: "WEB",
      });
      return;
    }
    const orderData = {
      eventId: event.eventId,
      userId: loggedInUser.user.userId,
      ticketItems: selectedTickets,
      mode: "ONLINE",
      gateWay: "PAYSTACK",
      channel: "WEB",
    };
    mutateInitializePayment({
      ...orderData,
    });
  };

  const formatDateTime = (date, time) => {
    const options = { day: "2-digit", month: "short", year: "numeric" };
    const formattedDate = new Date(date).toLocaleDateString("en-GB", options);
    return `${formattedDate}, ${time}`;
  };

  const handleLoginClick = () => {
    setShowLogin(true);
  };

  const handleBackToCart = () => {
    setShowLogin(false);
  };

  const handleSuccess = (data = null) => {
    if (data) {
      setData(data);
    }
    setPaymentInfo({
      amount: 0,
      email: "",
      reference: "",
      publicKey: "",
      transaction_charge: 0,
    });
    setCheckoutSuccess(true);
  };

  const handlePublicPayment = (data = null) => {
    if (data) {
      setData(data);
    }
    const orderData = {
      eventId: event.eventId,
      user: data.user,
      ticketItems: data.tickets,
      token: data.token,
      mode: "ONLINE",
      gateWay: "PAYSTACK",
      channel: "WEB",
    };
    mutateInitializePayment({
      ...orderData,
    });
  };

  const handlePaystackClose = () => {
    setPaymentInfo({
      amount: 0,
      email: "",
      reference: "",
      publicKey: "",
      transaction_charge: 0,
    });
  };

  const onLogout = () => {
    setShowLogin(false);
  };

  const onBackRegister = () => {
    handleBackToCart();
    setNoSignUp(false);
  };

  return (
    <div className="cart">
      <Paystack
        paymentInfo={paymentInfo}
        onSuccessPayment={handleSuccess}
        onClosePayment={handlePaystackClose}
      />
      {checkoutSuccess ? (
        <>
          <ThankYou
            host={event?.host}
            loggedInUser={loggedInUser}
            data={data}
            isMember={isMember}
          />
        </>
      ) : (
        <>
          <div className="cart-box-event">
            <div className="cart-left-section">
              {showLogin ? (
                <>
                  {noSignUp ? (
                    <>
                      <RegisterForm
                        onBack={onBackRegister}
                        onSuccess={handleSuccess}
                        onPayment={handlePublicPayment}
                        paymentInfo={paymentInfo}
                        tickets={tickets}
                        paidTickets={paidTickets}
                        event={event}
                      />
                    </>
                  ) : (
                    <>
                      <AuthForm
                        onBack={handleBackToCart}
                        defaultLogin={false}
                      />
                      <div className="no-signup-botton">
                        <button
                          className="checkout-button"
                          onClick={() => setNoSignUp(true)}
                        >
                          {"Get Ticket Without Signing Up"}
                        </button>
                      </div>
                    </>
                  )}
                </>
              ) : (
                <>
                  <div className="cart-header">
                    <div>
                      <h1>{event.name}</h1>
                      <p>{formatDateTime(event.startDate, event.startTime)}</p>
                    </div>
                    <UserLoginSwitch
                      onLogout={onLogout}
                      handleLoginClick={handleLoginClick}
                    />
                  </div>
                  <div className="cart-divider" />
                  <div className="cart-items-container">
                    {bundles.map((bundle) => (
                      <CartItem
                        key={bundle._id}
                        item={bundle}
                        onIncrease={handleIncrease}
                        onDecrease={handleDecrease}
                        isSoldOut={bundle.quantity - bundle.ticketSold <= 0}
                        isPurchaseDatePassed={
                          bundle.salesEndDate &&
                          new Date() > new Date(bundle.salesEndDate)
                        }
                        currency={currency}
                        maxTicketPerUser={event.maxTicketPerUser}
                      />
                    ))}
                  </div>
                  {error && <div className="error-message">{error}</div>}
                  <div className="cart-total">
                    <h2>Total: {formatCurrency(calculateTotal(), currency)}</h2>
                  </div>
                  <button
                    className="checkout-button"
                    onClick={handleSubmitOrder}
                    disabled={isLoadingFree || isLoadingPaid}
                  >
                    {isLoadingFree || isLoadingPaid
                      ? "Registering..."
                      : "Checkout"}
                  </button>
                </>
              )}
            </div>
            <div className="cart-right-section">
              {event.media && event.media.length > 0 && (
                <div className="cart-summary-image">
                  <ImageComponent
                    src={event.media[0].mediaThumb.replace(
                      "r200x200",
                      "r1500x1500",
                    )}
                    alt="Event"
                    className="event-media-thumb"
                  />
                </div>
              )}
              <CartSummary bundles={bundles} currency={currency} />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Cart;
